// components/AnnualBudgetTable.jsx
import { useEffect, useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import "./styles.css";
import { formatWithDots } from "../../../utils/utilsFunctions";

const AnnualBudgetTable = ({
  data = [],
  highlightMonth,
  type, // "money" | "units" / percent2dec
  showAnnual = true,
  showQuarter = true
}) => {
  const [selectedRow, setSelectedRow] = useState([]);

  const { rows, selected } = useMemo(() => {
    const selected = [];
    const result = [];
    const META = "95% ";
    const quarters = [
      "1º Trimestre",
      "2º Trimestre",
      "3º Trimestre",
      "4º Trimestre"
    ];

    let annual = { presupuesto: 0, ejecutado: 0 };
    let quarter = { presupuesto: 0, ejecutado: 0 };
    let qIndex = 0;

    data.forEach((row, idx) => {
      if (row.name === highlightMonth) selected.push(row);

      // ― Fila mensual
      result.push({
        ...row,
        real: row.presupuesto ? row.ejecutado / row.presupuesto : 0,
        meta: META,
        type: "month"
      });

      const p = Number(row.presupuesto) || 0;
      const e = Number(row.ejecutado) || 0;

      // ― Acumulados
      annual.presupuesto += p;
      annual.ejecutado += e;
      quarter.presupuesto += p;
      quarter.ejecutado += e;

      // ― Fin de trimestre cada tres filas
      if ((idx + 1) % 3 === 0) {
        const newRow = {
          name: quarters[qIndex],
          presupuesto: quarter.presupuesto,
          ejecutado: quarter.ejecutado,
          real: quarter.presupuesto
            ? quarter.ejecutado / quarter.presupuesto
            : 0,
          meta: META,
          type: "quarter"
        };

        if (showQuarter) {
          result.push(newRow);
          selected.push(newRow);
        }

        // reiniciar acumulador trimestral SIEMPRE
        quarter = { presupuesto: 0, ejecutado: 0 };
        qIndex++;
      }
    });

    // ― Fila anual opcional
    if (showAnnual) {
      result.push({
        name: "Anual",
        presupuesto: annual.presupuesto,
        ejecutado: annual.ejecutado,
        real: annual.presupuesto ? annual.ejecutado / annual.presupuesto : 0,
        meta: "",
        type: "annual"
      });
    }

    return { rows: result, selected };
  }, [data, highlightMonth, showAnnual, showQuarter]);

  // ─────────── Formatos ───────────
  const money = (v) => `$${(v || 0).toLocaleString("es-CO")}`;
  const normal = (v) => `${formatWithDots((v * 1).toFixed(0))}`;
  const percent = (v) => `${(v * 100).toFixed(0)}%`;
  const percent2dec = (v) => `${(v * 100).toFixed(2)}%`;

  // ─────────── Clases de fila ───────────

  const rowClassName = (row) => {
  const r = Array.isArray(row) ? row[0] : row;
  if (!r) return "";
  if (r.type === "quarter") return "quarter-row";
  if (r.type === "annual") return "annual-row";
  const meta = typeof r.meta === "string" ? parseFloat(r.meta) / 100 : r.meta;
  if (r.type !== "annual" && r.real < meta) return "below-target";
  return ""; // default sin clase
};

  // ─────────── Estilo de la celda REAL ───────────
  const realStyle = (row) =>
    row.type === "annual"
      ? { color: "white" }
      : {
          color: row.real >= row.meta ? "#4CAF50" : "#e53935",
          fontWeight: 500
        };

  useEffect(() => {
    setSelectedRow(selected);
  }, [selected, highlightMonth]);

  return (
    <div className="card annual-budget-table">
      <DataTable
        selectionMode="multiple"
        selection={selectedRow}
        dataKey="name"
        value={rows}
        rowClassName={rowClassName}
        showGridlines
      >
        <Column field="name" header="Mes" />
        <Column
          field="presupuesto"
          header="Presupuesto"
          body={(r) =>
            type === "units" ? normal(r.presupuesto) : money(r.presupuesto)
          }
          className="p-column-number"
        />
        <Column
          field="ejecutado"
          header="Ejecutado"
          body={(r) =>
            type === "units" ? normal(r.ejecutado) : money(r.ejecutado)
          }
          className="p-column-number"
        />
        <Column
          field="real"
          header="REAL"
          body={(r) =>
            type === "percent2dec" ? percent2dec(r.real) : percent(r.real)
          }
          className="p-column-number"
          bodyStyle={realStyle}
        />
        <Column
          field="meta"
          header="META"
          className="p-column-number p-column-meta"
        />
      </DataTable>
    </div>
  );
};

export default AnnualBudgetTable;
